<template>
    <pendo-popover-v2
        v-if="showPendoProducts"
        ref="pendoProductsPopover"
        :title="$t('pendoProducts.Pendo products')"
        placement="bottom-end"
        :popper-class="['pendo-products__popover', 'top-nav__popover']"
        :hover="false"
        keyboard-navigation
        @show="focusFirstOption"
        @hide="$emit('select')">
        <template #default="{ shown, ...triggerAttrs }">
            <button
                v-pendo-tooltip="{
                    content: $t('pendoProducts.Pendo products'),
                    arrow: true,
                }"
                v-bind="triggerAttrs"
                data-cy="pendo-products-dropdown-trigger"
                pendo-analytics="pendo-products-nav-item"
                class="top-nav__trigger top-nav__button-trigger"
                :class="{
                    'is-open': shown,
                }"
                :aria-label="$t('pendoProducts.Pendo products')"
                @keydown.tab="handleTabKeydown($event, shown, triggerAttrs)">
                <pendo-products-icon />
            </button>
        </template>
        <template #popover>
            <div
                ref="pendoProductsContent"
                class="top-nav__popover-content"
                data-cy="pendo-products-dropdown-content">
                <h2 class="nav-title">
                    {{ $t('pendoProducts.Pendo products') }}
                </h2>

                <pendo-router-link
                    v-for="option in dropdownOptions"
                    :key="option.id"
                    tabindex="0"
                    class="nav-option"
                    :aria-label="option.label"
                    :pendo-analytics="option.pendoAnalytics"
                    :to="option.route"
                    :target="option.target || '_self'"
                    :external="option.external"
                    @click="handleOptionClick($event)"
                    @keydown.space="handleOptionKeydown($event)"
                    @keydown.enter="handleOptionKeydown($event)"
                    @keydown.tab.prevent="handleOptionKeydown($event)">
                    <pendo-icon
                        v-if="option.id === 'pendo'"
                        type="circle"
                        class="pendo-products__active-dot"
                        fill="var(--text-body-default-base)"
                        stroke="var(--text-body-default-base)"
                        :size="6" />
                    <div
                        v-pendo-tooltip="{
                            content: tooltipContent,
                            arrow: true,
                            placement: 'top',
                            delay: { show: 1000, hide: 0 },
                        }"
                        class="nav-option__text"
                        @mouseover="onMouseover($event, option)">
                        {{ option.label }}
                    </div>
                </pendo-router-link>
            </div>
        </template>
    </pendo-popover-v2>
</template>

<script>
import { PendoTooltip, PendoPopoverV2, PendoIcon } from '@pendo/components';
import PendoProductsIcon from '@/top-nav/components/icons/pendo-products-icon.vue';
import PendoRouterLink from '@/vue-router/pendo-router-link.vue';

import { eventIsOpeningANewTabOrWindow } from '@/utils/window.utils.js';
import { generateFeedbackSSOUrl } from '@/utils/sso';
import { mapState, mapGetters } from 'vuex';
import { elementHasOverflowX } from '@/utils/dom.utils.js';

export default {
    name: 'PendoProductsDropdown',
    components: {
        PendoPopoverV2,
        PendoIcon,
        PendoProductsIcon,
        PendoRouterLink,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        preventTabKeyNavigation: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['select'],
    data () {
        return {
            tooltipContent: null,
        };
    },
    computed: {
        ...mapState({
            hosts: (state) => state.config.hosts,
        }),
        ...mapGetters({
            userCanAccessClassicFeedback: 'auth/userCanAccessClassicFeedback',
        }),
        showPendoProducts () {
            return this.userCanAccessClassicFeedback;
        },
        dropdownOptions () {
            return [
                {
                    id: 'pendo',
                    label: this.$t('pendoProducts.Pendo'),
                },
                {
                    id: 'feedback',
                    label: this.$t('pendoProducts.Feedback (Classic)'),
                    target: '_blank',
                    external: true,
                    route: generateFeedbackSSOUrl('/app', this.hosts),
                    pendoAnalytics: 'legacy-feedback',
                },
            ];
        },
    },
    methods: {
        handleOptionClick ($event) {
            if (eventIsOpeningANewTabOrWindow($event)) return;

            this.handleOptionSelect();
        },
        handleOptionKeydown ($event) {
            if (eventIsOpeningANewTabOrWindow($event)) return;

            $event.preventDefault();
            $event.currentTarget.click();
        },
        hide () {
            this.$refs.pendoProductsPopover?.hide?.();
        },
        handleOptionSelect () {
            this.hide();
            this.$emit('select');
        },
        focusFirstOption () {
            setTimeout(() => {
                const firstOption = this.$refs.pendoProductsContent?.querySelector('.nav-option');
                if (firstOption) {
                    firstOption.focus();
                }
            }, 50);
        },
        onMouseover ({ currentTarget }, option) {
            const { clientWidth, scrollWidth } = currentTarget;
            const hasOverflow = elementHasOverflowX({ clientWidth, scrollWidth });

            if (hasOverflow) {
                this.tooltipContent = option.label;
            } else {
                this.tooltipContent = null;
            }
        },
        handleTabKeydown ($event, shown, triggerAttrs) {
            if (this.preventTabKeyNavigation) {
                $event.preventDefault();

                if (shown) {
                    triggerAttrs.hide();
                } else {
                    triggerAttrs.show();
                }
            }
        },
    },
};
</script>

<style lang="less" scoped>
@import '@/side-nav/nav.style.less';

.top-nav__button-trigger:focus:not(:focus-visible) {
    outline: unset;
}

.pendo-products {
    &__active-dot {
        margin-right: 8px;

        :deep(svg) {
            height: 8px;
            width: 8px;
        }
    }
}
</style>

<style lang="less">
.top-nav__popover.pendo-products__popover {
    .pendo-popover-v2__panel {
        width: 210px;
        padding: 0;
    }
}
</style>